import React from 'react';
import { useTranslation } from 'react-i18next';
import { PACKAGE_FEATURES, PACKAGE_LEVEL } from '@src/constants/package';
import { useSelector } from 'react-redux';
import {
  checkCustomPackage,
  checkFreePackage,
  formatNumberWithSuffix,
  getCustomPackagePrice,
  roundUpToOneDecimal,
} from '@src/services/package';
import {
  calculateCredits,
  calculateMinutes,
} from '@src/services/packageComparison';
import { RATE_CREDITS_PER_CHARACTER_ADVANCED } from '@src/constants/packageComparison';
import { getOptimalVouchersForPackage } from '@src/services/voucher';
import { checkVietNam } from '@src/utils/checkCountry';
import CheckOrCross from '../../Shared/CheckOrCross';
import { getCreditsWithBestVoucher } from '../../Credits/CreditsUsed';

const ShowCredits = ({ pkg, canUseVouchers, totalVoucher }) => {
  const isVietNam = checkVietNam();
  const bestVoucher = canUseVouchers(pkg)?.[0];
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const optionsSelected = useSelector(
    (state) => state.packages.optionsSelected,
  );

  const isAdvancedVoice = [
    PACKAGE_FEATURES.ADVANCED_VIETNAM_VOICE,
    PACKAGE_FEATURES.ADVANCED_GLOBAL_VOICE,
  ].some((feature) => pkg?.features?.includes(feature));

  if (!isAdvancedVoice) {
    return <CheckOrCross key={pkg?.code} isCheck={false} />;
  }

  const isCustomPackage = checkCustomPackage({
    type: pkg?.type,
    level: pkg?.level,
  });

  if (isCustomPackage) {
    const { price: priceVipPlusPackagePrice, vndPrice } = getCustomPackagePrice(
      {
        credits: optionsSelected.credits,
        voiceClonings: optionsSelected.voiceClonings,
        devices: optionsSelected.devices,
        concurrentRequestApi: optionsSelected.concurrentRequestApi,
        customResource: pkg?.customResource,
        isVietNam,
      },
    );

    const [bestVoucherCustomPkg] = getOptimalVouchersForPackage({
      pkg: {
        ...pkg,
        price: priceVipPlusPackagePrice,
        vndPrice,
        usdPrice: priceVipPlusPackagePrice,
        custom: { credits: optionsSelected.credits },
      },
      vouchers: totalVoucher,
    });

    const creditsWithBestVoucher = getCreditsWithBestVoucher({
      bestVoucher: bestVoucherCustomPkg,
      credits: optionsSelected?.credits,
      level: PACKAGE_LEVEL.CUSTOM,
      totalCycles: 1,
    });
    const creditsPerYear = formatNumberWithSuffix(
      calculateCredits(
        creditsWithBestVoucher,
        RATE_CREDITS_PER_CHARACTER_ADVANCED,
      ),
      language,
    );
    const minutesPerYear = calculateMinutes(
      creditsWithBestVoucher,
      RATE_CREDITS_PER_CHARACTER_ADVANCED,
    );

    const formatTime =
      minutesPerYear > 60
        ? roundUpToOneDecimal(minutesPerYear / 60)
        : minutesPerYear;
    const formatTimeUnit = minutesPerYear > 60 ? t('hours') : t('minutes');

    return (
      <div
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{
          __html: t('creditsPerYear', {
            creditsPerYear: creditsPerYear.toLocaleString(language),
            minutesPerYear: formatTime.toLocaleString(language),
            unit: formatTimeUnit.toLowerCase(),
          }),
        }}
      />
    );
  }

  const isFreePackage = checkFreePackage(pkg?.code);
  const creditsWithBestVoucher = isFreePackage
    ? pkg?.bonusCharacters
    : getCreditsWithBestVoucher({
        bestVoucher,
        credits: pkg?.cycle?.creditsPerCycle,
        level: pkg?.level,
        totalCycles: pkg?.cycle?.totalCycles,
      });

  const creditsPerCycle = calculateCredits(
    creditsWithBestVoucher,
    RATE_CREDITS_PER_CHARACTER_ADVANCED,
  );
  const minutesPerCycle = calculateMinutes(
    creditsWithBestVoucher,
    RATE_CREDITS_PER_CHARACTER_ADVANCED,
  );
  const formatTime =
    minutesPerCycle > 60
      ? roundUpToOneDecimal(minutesPerCycle / 60)
      : minutesPerCycle;
  const formatTimeUnit = minutesPerCycle > 60 ? t('hours') : t('minutes');

  const translationKey = isFreePackage ? 'creditsPerDay' : 'creditsPerMonthV2';
  const translationParams = isFreePackage
    ? {
        creditsPerDay: creditsPerCycle.toLocaleString(language),
        minutesPerDay: formatTime.toLocaleString(language),
        unit: formatTimeUnit.toLowerCase(),
      }
    : {
        creditsPerMonth: formatNumberWithSuffix(creditsPerCycle, language),
        minutesPerMonth: formatTime.toLocaleString(language),
        unit: formatTimeUnit.toLowerCase(),
      };

  return (
    <div
      /* eslint-disable-next-line react/no-danger */
      dangerouslySetInnerHTML={{
        __html: t(translationKey, translationParams),
      }}
    />
  );
};

const advancedVoiceFeatures = ({ packages, canUseVouchers, totalVoucher }) =>
  packages.map((pkg) => (
    <ShowCredits
      key={pkg.code}
      pkg={pkg}
      canUseVouchers={canUseVouchers}
      totalVoucher={totalVoucher}
    />
  ));

export default advancedVoiceFeatures;
